import { useMemo } from "react";
import { useWatch, type Control } from "react-hook-form";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import { getFinishedDeckSectionLayout } from "./finishedDeckSectionGeometry";
import type { SectionPreviewSize } from "./ZoomableSectionPreview";

export const useFinishedDeckSectionGeometry = (
  control: Control<DeckDesignerConfigurationFormValues>,
  previewSize: SectionPreviewSize,
) => {
  const totalHeight = useWatch({ control, name: "totalHeight", exact: true });
  const boardWidth = useWatch({ control, name: "boardWidth", exact: true });
  const boardThickness = useWatch({ control, name: "boardThickness", exact: true });
  const groovingHeight = useWatch({ control, name: "groovingHeight", exact: true });
  const groovingType = useWatch({ control, name: "groovingType", exact: true });
  const gap = useWatch({ control, name: "gap", exact: true });
  const fasteningType = useWatch({ control, name: "fasteningType", exact: true });
  const connectorFamily = useWatch({ control, name: "connectorFamily", exact: true });
  const connector = useWatch({ control, name: "connector", exact: true });
  const connectorSubstructureScrew = useWatch({ control, name: "connectorSubstructureScrew", exact: true });
  const connectorBoardScrew = useWatch({ control, name: "connectorBoardScrew", exact: true });
  const secondaryItem = useWatch({ control, name: "secondaryItem", exact: true });
  const substructureMaterial = useWatch({ control, name: "substructureMaterial", exact: true });
  const substructure = useWatch({ control, name: "substructure", exact: true });
  const supportFamily = useWatch({ control, name: "supportFamily", exact: true });
  const support = useWatch({ control, name: "support", exact: true });

  const layout = useMemo(
    () =>
      getFinishedDeckSectionLayout(
        {
          totalHeight,
          boardWidth,
          boardThickness,
          groovingHeight,
          groovingType,
          gap,
          fasteningType,
          connectorFamily,
          connector,
          connectorSubstructureScrew,
          connectorBoardScrew,
          secondaryItem,
          substructureMaterial,
          substructure,
          supportFamily,
          support,
        },
        previewSize,
      ),
    [
      boardThickness,
      boardWidth,
      connector,
      connectorBoardScrew,
      connectorFamily,
      connectorSubstructureScrew,
      fasteningType,
      gap,
      groovingHeight,
      groovingType,
      previewSize,
      secondaryItem,
      substructure,
      substructureMaterial,
      support,
      supportFamily,
      totalHeight,
    ],
  );

  // What the preview shows: its zoom resets whenever this changes (see ZoomableSectionPreview) - only
  // rebuilt when one of these values does, not on every render.
  const resetKey = useMemo(
    () =>
      JSON.stringify({
        totalHeight,
        boardWidth,
        boardThickness,
        gap,
        fasteningType,
        connectorFamily,
        connector,
        connectorSubstructureScrew,
        connectorBoardScrew,
        secondaryItem,
        substructureMaterial,
        substructure,
        supportFamily,
        support,
      }),
    [
      boardThickness,
      boardWidth,
      connector,
      connectorBoardScrew,
      connectorFamily,
      connectorSubstructureScrew,
      fasteningType,
      gap,
      secondaryItem,
      substructure,
      substructureMaterial,
      support,
      supportFamily,
      totalHeight,
    ],
  );

  return { layout, resetKey };
};
